import React from "react";
import {
  computeFootageProgress,
  computeFootageProgressForProject,
  formatFootageNumber,
} from "./expansionProgressUtils";
import "./expansionFootageProgressBar.css";

function FootageProgressRow({ label, tone, completed, target, pct, height }) {
  const hasTarget = target != null && target > 0;
  const statLabel = hasTarget
    ? `${formatFootageNumber(completed)} of ${formatFootageNumber(target)} ft`
    : `${formatFootageNumber(completed)} ft`;

  return (
    <div className="expansion-footage-bar-row">
      <div
        className="expansion-footage-bar"
        style={{ height }}
        role="img"
        aria-label={`${label} ${statLabel} installed`}
      >
        <div className="expansion-footage-bar-track">
          {pct > 0 ? (
            <div
              className={`expansion-footage-bar-segment expansion-footage-bar-segment--${tone}`}
              style={{ width: `${pct}%` }}
            />
          ) : null}
        </div>
      </div>
      {label ? (
        <div className="expansion-footage-progress-meta">
          <span className="expansion-footage-progress-stat">
            <span
              className={`expansion-footage-legend-dot expansion-footage-legend-dot--${tone}`}
              aria-hidden
            />
            {label} {statLabel}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Installation bars: conduit (construction ft target) and fiber (override or construction ft target).
 */
export default function ExpansionFootageProgressBar({
  subSummary,
  projectRow,
  progress: progressOverride,
  height = 10,
  showLegend = true,
  className = "",
}) {
  const progress =
    progressOverride ??
    (projectRow
      ? computeFootageProgressForProject(projectRow)
      : computeFootageProgress(subSummary));
  const {
    fiberDenominator,
    conduitDenominator,
    conduitCompleted,
    fiberCompleted,
    proofingCompleted,
    completedTotal,
    vaults,
    conduitPct,
    fiberPct,
  } = progress;

  const showConduit = conduitDenominator != null && conduitDenominator > 0;
  const showFiber = fiberDenominator != null && fiberDenominator > 0;

  const ariaParts = [];
  if (showConduit) {
    ariaParts.push(
      `Conduit ${formatFootageNumber(conduitCompleted)} of ${formatFootageNumber(conduitDenominator)} feet`
    );
  }
  if (showFiber) {
    ariaParts.push(
      `Fiber ${formatFootageNumber(fiberCompleted)} of ${formatFootageNumber(fiberDenominator)} feet`
    );
  }
  if (proofingCompleted > 0) {
    ariaParts.push(`Proofing ${formatFootageNumber(proofingCompleted)} feet`);
  }
  if (!ariaParts.length) {
    ariaParts.push(`${formatFootageNumber(completedTotal)} feet installed`);
  }

  return (
    <div
      className={`expansion-footage-progress ${className}`.trim()}
      role="img"
      aria-label={ariaParts.join(". ")}
    >
      {showConduit ? (
        <FootageProgressRow
          label={showLegend ? "Conduit" : ""}
          tone="conduit"
          completed={conduitCompleted}
          target={conduitDenominator}
          pct={conduitPct}
          height={height}
        />
      ) : null}
      {showFiber ? (
        <FootageProgressRow
          label={showLegend ? "Fiber" : ""}
          tone="fiber"
          completed={fiberCompleted}
          target={fiberDenominator}
          pct={fiberPct}
          height={height}
        />
      ) : null}
      {!showConduit && !showFiber && showLegend ? (
        <div className="expansion-footage-progress-meta">
          <span className="expansion-footage-progress-stat expansion-footage-progress-stat--muted">
            No footage targets set
          </span>
        </div>
      ) : null}
      {showLegend && proofingCompleted > 0 ? (
        <div className="expansion-footage-progress-meta">
          <span className="expansion-footage-progress-stat expansion-footage-progress-stat--muted">
            {formatFootageNumber(proofingCompleted)} ft proofing
          </span>
        </div>
      ) : null}
      {showLegend && vaults > 0 ? (
        <div className="expansion-footage-progress-meta">
          <span className="expansion-footage-progress-stat expansion-footage-progress-stat--muted">
            {formatFootageNumber(vaults)} vault{vaults === 1 ? "" : "s"}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export { computeFootageProgress, computeFootageProgressForProject, formatFootageNumber };
